import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Lang = 'en' | 'tr';

export const LANGS: readonly Lang[] = ['en', 'tr'];

export const LANG_LABELS: Record<Lang, string> = {
  en: 'EN',
  tr: 'TR',
};

/**
 * Every string the interface can show.
 *
 * Kept as one flat object per language so a missing translation is a type
 * error rather than a silent fallback to English.
 */
const STRINGS = {
  en: {
    'nav.leaderboard': 'Leaderboard',
    'nav.account': 'Account',
    'nav.tutorial': 'How to play',
    'nav.theme': 'Theme',
    'nav.language': 'Language',
    'nav.performance': 'Performance',
    'nav.sound': 'Sound',
    'sound.on': 'On',
    'sound.off': 'Off',

    'difficulty.beginner': 'Beginner',
    'difficulty.intermediate': 'Intermediate',
    'difficulty.expert': 'Expert',
    'difficulty.custom': 'Custom',

    'game.hint': 'Right-click or long-press to flag',
    'game.hintFlagMode': 'Flag mode on — tap to flag, long-press or right-click to reveal',
    'game.flagModeHint': 'Flag mode: tap flags instead of revealing, long-press or right-click reveals',
    'game.newBoard': 'New board',
    'game.minesLeft': 'Mines left',
    'game.elapsed': 'Elapsed',

    'custom.width': 'Width',
    'custom.height': 'Height',
    'custom.mines': 'Mines',
    'custom.set': 'Set',
    'custom.unranked': 'Custom boards are never ranked.',

    'toast.boom': 'Boom',
    'toast.cleared': 'Cleared',
    'toast.notRanked': 'Not ranked',
    'toast.notTimed': 'Saved — too late to be timed',
    'toast.personalBest': 'Personal best',
    'toast.rank': 'Rank #{rank}',
    'toast.dealing': 'Dealing a board…',
    'toast.verifying': 'Verifying…',
    'toast.saveFailed': "Couldn't save your game",
    'toast.willRetry': 'Retrying automatically…',
    'toast.retryNow': 'Retry now',
    'toast.streak': '🔥 {n}-day streak',
    'toast.levelUp': '⭐ Level {n}',
    'toast.progressXp': '+{n} XP',

    'board.difficulty': 'Difficulty',
    'board.sort': 'Sort',
    'board.period': 'Period',
    'board.sort.wins': 'Most wins',
    'board.sort.time': 'Best time',
    'board.period.daily': 'Today',
    'board.period.weekly': 'This week',
    'board.period.all': 'All time',
    'board.empty': 'Nothing here yet.',
    'board.loading': 'Loading…',
    'board.offline': 'Offline.',
    'board.rank': '#',
    'board.player': 'Player',
    'board.wins': 'Wins',
    'board.best': 'Best',
    'board.time': 'Time',
    'board.when': 'When',
    'board.levels': 'All',
    'board.level': 'Level',
    'board.xp': 'XP',

    'account.title': 'Account',
    'account.playRanked': 'Play ranked',
    'account.pickName': 'Pick a name. You get a key to sign in anywhere.',
    'account.username': 'username',
    'account.create': 'Create',
    'account.haveKey': 'Already have a key?',
    'account.pasteKey': 'paste your key',
    'account.signIn': 'Sign in',
    'account.signedInAs': 'Signed in as',
    'account.keyWarning': 'Your key is your account. Keep it like a password.',
    'account.show': 'Show key',
    'account.hide': 'Hide key',
    'account.copy': 'Copy',
    'account.signOut': 'Sign out',
    'account.streak': 'Day streak',
    'account.bestStreak': 'Best streak',
    'account.wins': 'Wins',
    'account.played': 'Played',
    'account.status': 'Status',
    'account.preferences': 'Preferences',
    'account.level': 'Level {n}',
    'account.xpProgress': '{have} / {need} XP',
    'perf.auto': 'Auto',
    'perf.low': 'Low',
    'perf.hint': 'Low turns off blur and animation, for slower machines.',

    'account.feedback': 'Feedback',
    'account.feedbackHint': 'Found a bug, or have an idea? Email it straight to the developer.',
    'account.reportBug': 'Report a bug',

    'tutorial.title': 'How to play',
    'tutorial.start': 'Start',
    'tutorial.next': 'Next',
    'tutorial.back': 'Back',
    'tutorial.done': 'Done',
    'tutorial.replay': 'Play again',
    'tutorial.step': 'Step {n} of {total}',
    'tutorial.s1.title': 'Open a square',
    'tutorial.s1.body':
      'Click any square to uncover it. The first click is always safe — the mines are placed around it.',
    'tutorial.s2.title': 'Numbers count nearby mines',
    'tutorial.s2.body':
      'An open square with a number is safe — the number just says how many mines are hiding in the up-to-8 squares touching it. For example, a square showing 3 has exactly 3 mines among its neighbours, no more, no fewer. An empty (blank) square has zero mines next to it, which is why opening one clears a whole area at once.',
    'tutorial.s3.title': 'Flag what you are sure about',
    'tutorial.s3.body':
      'Right-click (or long-press on a phone) to plant a flag on a square you are sure hides a mine. Flags do not open anything by themselves — they are just your own notes, and they stop you from opening that square by accident.',
    'tutorial.s4.title': 'Chording: clear several squares at once',
    'tutorial.s4.body':
      'Say a number shows 2, and you have already flagged 2 of its neighbours as mines. Click that number itself (not a neighbour) and every one of its remaining, unflagged neighbours opens automatically — because you have already accounted for all its mines. This is the shortcut experienced players use to clear a board fast, instead of opening each safe square by hand.',
    'tutorial.s5.title': 'Clear every safe square',
    'tutorial.s5.body':
      'You win when every square without a mine is open. You never have to click a mine to finish — the flags are optional.',

    'tutorial.live.intro':
      "Goal: uncover every square that isn't a mine. Click a mine and it's game over. Up top, the left counter shows mines left to flag and the right one is your timer; the face in the middle shows a new board any time you click it. This board is just for practice — nothing here is scored.",
    'tutorial.live.introFlagMode':
      "Goal: uncover every square that isn't a mine. Click a mine and it's game over. Flag mode is on by default: a tap flags a square, and long-press (or right-click) reveals it instead — switch it off any time from the flag icon above the board if you'd rather tap to reveal. Up top, the left counter shows mines left to flag and the right one is your timer; the face in the middle shows a new board any time you click it. This board is just for practice — nothing here is scored.",
    'tutorial.live.reveal': 'Tap any covered square to reveal it. Go ahead — this board is just for practice.',
    'tutorial.live.revealFlagMode':
      'Long-press (or right-click on a laptop) any covered square to reveal it. Go ahead — this board is just for practice.',
    'tutorial.live.numbersWithExample':
      'See the {n}? That means exactly {n} of its neighbours are mines — no more, no fewer.',
    'tutorial.live.numbersNoExample':
      'That whole area had no mines touching it, so it all opened at once. A number, when you see one, tells you exactly how many mines are in its neighbours.',
    'tutorial.live.flag': 'Now right-click (or long-press on a phone) a square you think hides a mine, to flag it.',
    'tutorial.live.flagFlagMode': 'Now tap a square you think hides a mine, to flag it.',
    'tutorial.live.flagWithHint':
      'The highlighted square is a real mine. Right-click it (or long-press on a phone) to flag it.',
    'tutorial.live.flagWithHintFlagMode': 'The highlighted square is a real mine. Tap it to flag it.',
    'tutorial.live.flagExplain':
      "Flags don't open anything by themselves — they're just your own notes, so you don't open that square by accident.",
    'tutorial.live.chordPrompt':
      'That highlighted number already has all its mines flagged. Click the number itself — not a neighbour — to open the rest at once.',
    'tutorial.live.chordExplainOnly':
      "One more trick: once a number has all its mines flagged, click the number itself and every remaining neighbour opens at once. Keep an eye out for it — it's the fast way to clear a board.",
    'tutorial.live.win':
      "That's the whole game: clear every square that is not a mine to win, and you never have to click a mine to finish. Outside this tutorial, pick Beginner, Intermediate or Expert above the board for a bigger challenge, or set a Custom size. Sign in from the Account tab to save your times to the leaderboard — practice boards like this one are never ranked.",
    'tutorial.live.exit': 'Exit tutorial',
    'tutorial.live.skip': 'Skip this step',
    'tutorial.live.waiting': 'Waiting for you to try it…',

    'theme.light': 'Light',
    'theme.dark': 'Dark',
  },

  tr: {
    'nav.leaderboard': 'Sıralama',
    'nav.account': 'Hesap',
    'nav.tutorial': 'Nasıl oynanır',
    'nav.theme': 'Tema',
    'nav.language': 'Dil',
    'nav.performance': 'Performans',
    'nav.sound': 'Ses',
    'sound.on': 'Açık',
    'sound.off': 'Kapalı',

    'difficulty.beginner': 'Başlangıç',
    'difficulty.intermediate': 'Orta',
    'difficulty.expert': 'Uzman',
    'difficulty.custom': 'Özel',

    'game.hint': 'Bayrak için sağ tıkla veya basılı tut',
    'game.hintFlagMode': 'Bayrak modu açık — bayraklamak için dokun, açmak için sağ tıkla veya basılı tut',
    'game.flagModeHint': 'Bayrak modu: dokunuş açmak yerine bayraklar, basılı tutmak veya sağ tık açar',
    'game.newBoard': 'Yeni oyun',
    'game.minesLeft': 'Kalan mayın',
    'game.elapsed': 'Süre',

    'custom.width': 'Genişlik',
    'custom.height': 'Yükseklik',
    'custom.mines': 'Mayın',
    'custom.set': 'Ayarla',
    'custom.unranked': 'Özel tahtalar sıralamaya girmez.',

    'toast.boom': 'Patladı',
    'toast.cleared': 'Temizlendi',
    'toast.notRanked': 'Sıralama dışı',
    'toast.notTimed': 'Kaydedildi — süre sıralamaya girmedi',
    'toast.personalBest': 'Kişisel rekor',
    'toast.rank': '#{rank}. sıra',
    'toast.dealing': 'Tahta hazırlanıyor…',
    'toast.verifying': 'Doğrulanıyor…',
    'toast.saveFailed': 'Oyunun kaydedilemedi',
    'toast.willRetry': 'Otomatik olarak tekrar denenecek…',
    'toast.retryNow': 'Şimdi dene',
    'toast.streak': '🔥 {n} günlük seri',
    'toast.levelUp': '⭐ Seviye {n}',
    'toast.progressXp': '+{n} XP',

    'board.difficulty': 'Zorluk',
    'board.sort': 'Sıralama',
    'board.period': 'Dönem',
    'board.sort.wins': 'En çok galibiyet',
    'board.sort.time': 'En iyi süre',
    'board.period.daily': 'Bugün',
    'board.period.weekly': 'Bu hafta',
    'board.period.all': 'Tüm zamanlar',
    'board.empty': 'Burada henüz bir şey yok.',
    'board.loading': 'Yükleniyor…',
    'board.offline': 'Çevrimdışı.',
    'board.rank': '#',
    'board.player': 'Oyuncu',
    'board.wins': 'Galibiyet',
    'board.best': 'En iyi',
    'board.time': 'Süre',
    'board.when': 'Ne zaman',
    'board.levels': 'Tümü',
    'board.level': 'Seviye',
    'board.xp': 'XP',

    'account.title': 'Hesap',
    'account.playRanked': 'Sıralamada oyna',
    'account.pickName': 'Bir isim seç. Her yerde giriş yapmak için bir anahtar alırsın.',
    'account.username': 'kullanıcı adı',
    'account.create': 'Oluştur',
    'account.haveKey': 'Anahtarın var mı?',
    'account.pasteKey': 'anahtarını yapıştır',
    'account.signIn': 'Giriş yap',
    'account.signedInAs': 'Giriş yapıldı:',
    'account.keyWarning': 'Anahtarın hesabındır. Şifre gibi sakla.',
    'account.show': 'Anahtarı göster',
    'account.hide': 'Anahtarı gizle',
    'account.copy': 'Kopyala',
    'account.signOut': 'Çıkış yap',
    'account.streak': 'Günlük seri',
    'account.bestStreak': 'En iyi seri',
    'account.wins': 'Galibiyet',
    'account.played': 'Oynanan',
    'account.status': 'Durum',
    'account.preferences': 'Tercihler',
    'account.level': 'Seviye {n}',
    'account.xpProgress': '{have} / {need} XP',
    'perf.auto': 'Otomatik',
    'perf.low': 'Düşük',
    'perf.hint': 'Düşük modu, yavaş bilgisayarlar için bulanıklığı ve animasyonu kapatır.',

    'account.feedback': 'Geri bildirim',
    'account.feedbackHint': 'Bir hata mı buldun, ya da bir fikrin mi var? Doğrudan geliştiriciye e-posta at.',
    'account.reportBug': 'Hata bildir',

    'tutorial.title': 'Nasıl oynanır',
    'tutorial.start': 'Başla',
    'tutorial.next': 'İleri',
    'tutorial.back': 'Geri',
    'tutorial.done': 'Bitti',
    'tutorial.replay': 'Tekrar oyna',
    'tutorial.step': 'Adım {n} / {total}',
    'tutorial.s1.title': 'Bir kare aç',
    'tutorial.s1.body':
      'Açmak için herhangi bir kareye tıkla. İlk tıklama her zaman güvenlidir — mayınlar onun etrafına yerleştirilir.',
    'tutorial.s2.title': 'Sayılar yakındaki mayınları sayar',
    'tutorial.s2.body':
      'Açılmış bir sayı güvenlidir — sayı sadece ona komşu olan (en fazla 8) karede kaç mayın gizlendiğini söyler. Örneğin 3 yazan bir karenin komşularında tam olarak 3 mayın vardır, ne fazla ne eksik. Boş (sayısız) bir karenin komşularında hiç mayın yoktur, bu yüzden böyle bir kareyi açmak bir bölgeyi birden temizler.',
    'tutorial.s3.title': 'Emin olduğun yere bayrak dik',
    'tutorial.s3.body':
      'Mayın olduğundan emin olduğun bir kareye bayrak dikmek için sağ tıkla (telefonda basılı tut). Bayraklar tek başına bir şey açmaz — sadece senin notlarındır ve o kareyi yanlışlıkla açmanı engeller.',
    'tutorial.s4.title': 'Akor: birden fazla kareyi aynı anda aç',
    'tutorial.s4.body':
      'Diyelim bir sayı 2 gösteriyor ve komşularından 2 tanesini zaten bayrakladın. O zaman sayının kendisine tıkla (komşularına değil) — bayraksız kalan bütün komşuları otomatik olarak açılır, çünkü mayınların hepsini zaten işaretledin. Deneyimli oyuncular tahtayı böyle hızlı temizler, her güvenli kareyi teker teker açmak yerine.',
    'tutorial.s5.title': 'Bütün güvenli kareleri aç',
    'tutorial.s5.body':
      'Mayınsız bütün kareler açıldığında kazanırsın. Bitirmek için mayına tıklaman gerekmez — bayraklar isteğe bağlıdır.',

    'tutorial.live.intro':
      'Amaç: mayın olmayan bütün kareleri açmak. Bir mayına tıklarsan oyun biter. Üstteki sol sayaç bayraklanacak mayın sayısını, sağdaki ise süreni gösterir; ortadaki yüze her tıkladığında yeni bir tahta gelir. Bu tahta sadece pratik için — burada hiçbir şey puanlanmaz.',
    'tutorial.live.introFlagMode':
      'Amaç: mayın olmayan bütün kareleri açmak. Bir mayına tıklarsan oyun biter. Bayrak modu varsayılan olarak açık: dokunuş bir kareyi bayraklar, basılı tutmak (veya sağ tık) ise açar — dokunuşla açmayı tercih edersen tahtanın üstündeki bayrak simgesinden istediğin an kapatabilirsin. Üstteki sol sayaç bayraklanacak mayın sayısını, sağdaki ise süreni gösterir; ortadaki yüze her tıkladığında yeni bir tahta gelir. Bu tahta sadece pratik için — burada hiçbir şey puanlanmaz.',
    'tutorial.live.reveal': 'Açmak için herhangi bir kareye dokun. Hadi dene — bu tahta sadece pratik için.',
    'tutorial.live.revealFlagMode':
      'Açmak için herhangi bir kareye basılı tut (bilgisayarda sağ tıkla). Hadi dene — bu tahta sadece pratik için.',
    'tutorial.live.numbersWithExample':
      '{n} sayısını görüyor musun? Bu, komşularından tam olarak {n} tanesinin mayın olduğu anlamına gelir — ne fazla ne eksik.',
    'tutorial.live.numbersNoExample':
      'O bölgenin hiçbir komşusunda mayın yoktu, bu yüzden hepsi birden açıldı. Bir sayı gördüğünde, o kareye komşu kaç mayın olduğunu tam olarak söyler.',
    'tutorial.live.flag': 'Şimdi mayın olduğunu düşündüğün bir kareye sağ tıkla (telefonda basılı tut) ve bayrak dik.',
    'tutorial.live.flagFlagMode': 'Şimdi mayın olduğunu düşündüğün bir kareye dokun ve bayrak dik.',
    'tutorial.live.flagWithHint':
      'İşaretlenen kare gerçek bir mayın. Bayraklamak için sağ tıkla (telefonda basılı tut).',
    'tutorial.live.flagWithHintFlagMode': 'İşaretlenen kare gerçek bir mayın. Bayraklamak için dokun.',
    'tutorial.live.flagExplain':
      'Bayraklar tek başına bir şey açmaz — sadece senin notlarındır, o kareyi yanlışlıkla açmanı engellerler.',
    'tutorial.live.chordPrompt':
      'İşaretlenen sayının bütün mayınları zaten bayraklanmış. Sayının kendisine tıkla — komşusuna değil — kalan hepsi birden açılsın.',
    'tutorial.live.chordExplainOnly':
      'Bir ipucu daha: bir sayının bütün mayınları bayraklandığında, sayının kendisine tıkla ve kalan bütün komşular aynı anda açılır. Buna dikkat et — tahtayı hızlı temizlemenin yolu budur.',
    'tutorial.live.win':
      'İşte oyunun tamamı bu: mayın olmayan bütün kareleri açarak kazanırsın, bitirmek için mayına tıklaman gerekmez. Bu öğreticinin dışında, üstteki Başlangıç, Orta veya Uzman seçeneklerinden birini seç, ya da Özel bir boyut ayarla. Sürelerini sıralamaya kaydetmek için Hesap sekmesinden giriş yap — bunun gibi pratik tahtaları asla sıralamaya girmez.',
    'tutorial.live.exit': 'Öğreticiden çık',
    'tutorial.live.skip': 'Bu adımı geç',
    'tutorial.live.waiting': 'Denemeni bekliyorum…',

    'theme.light': 'Açık',
    'theme.dark': 'Koyu',
  },
} as const;

export type StringKey = keyof (typeof STRINGS)['en'];

const STORAGE_KEY = 'minesweeper.lang.v1';

function detectLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'tr') return stored;
  } catch {
    // Private browsing; fall through to the browser's own preference.
  }
  return navigator.language?.toLowerCase().startsWith('tr') ? 'tr' : 'en';
}

interface I18n {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Looks up a string, substituting `{name}` placeholders. */
  t: (key: StringKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18n | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [lang, setLangState] = useState<Lang>(detectLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<I18n>(
    () => ({
      lang,
      setLang: (next) => {
        setLangState(next);
        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // Not being able to remember the choice is not worth failing over.
        }
      },
      t: (key, vars) => {
        const template: string = STRINGS[lang][key] ?? STRINGS.en[key] ?? key;
        if (!vars) return template;
        return template.replace(/\{(\w+)\}/g, (match, name: string) =>
          name in vars ? String(vars[name]) : match,
        );
      },
    }),
    [lang],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18n {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used inside <I18nProvider>');
  return context;
}
